import React from "react";
import styled from "styled-components";
import { Button, Progress, Table } from "antd";
import { DownloadOutlined, CreditCardOutlined } from "@ant-design/icons";
import { Content, MainContainer, MainContent, Title } from "../../../styles/styles";
import SideBar from "../../../components/nav/SideBar";
import { useNavigationStore } from "store/navigationStore";
import { useNavigate } from "react-router-dom";
import { KoHeadline } from "../../../styles/sharedStyles";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const ManagementSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  background: #fff;
  border-radius: 20px;
  padding: 40px;
  color: #000;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.04);
`;

const BillingHistorySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FreePlanSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
`;

const PlanHeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
`;

const PlanContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PlanTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #000;
`;

const PlanDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #888;
  line-height: 1.6;
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProgressLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;


const UpgradeButton = styled(Button)`
  background: #000;
  border: none;
  color: #fff;
  padding: 14px 28px;
  height: auto;
  font-weight: 500;
  border-radius: 8px;
  font-size: 15px;
  
  &:hover {
    background: #333 !important;
    color: #fff !important;
  }
`;

const PaymentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-left: 1px solid #e5e5e5;
  padding-left: 40px;
`;

const PaymentHeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
`;

const PaymentContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PaymentTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #000;
`;

const PaymentDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #888;
  line-height: 1.6;
`;

const CardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0;
`;

const VisaIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
`;

const CardDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const CardNumber = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #333;
`;

const CardExpiry = styled.div`
  font-size: 14px;
  color: #888;
`;

const ChangeButton = styled(Button)`
  /* bt */
  box-sizing: border-box;
  
  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  
  width: 123px;
  height: 40px;
  
  background: transparent;
  /* Font Color 3 */
  border: 1px solid #727272;
  border-radius: 4px;
  
  /* Inside auto layout */
  flex: none;
  order: 1;
  flex-grow: 0;
  
  &:hover {
    background: #f8f8f8 !important;
    border-color: #727272 !important;
    color: inherit !important;
  }
`;

const BottomSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

const DownloadButton = styled(Button)`
  background: #fff;
  border: 1px solid #666;
  color: #000;
  padding: 8px 16px;
  height: auto;
  font-weight: 500;
  border-radius: 8px;
  
  &:hover {
    background: #f0f0f0 !important;
    border-color: #333 !important;
    color: #000 !important;
  }
`;

const StyledTable = styled(Table)`
  width: 100%;
  
  .ant-table {
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    width: 100%;
  }
  
  .ant-table-thead > tr > th {
    background: #fff;
    border-bottom: 2px solid #f0f0f0;
    border-left: none !important;
    border-right: none !important;
    font-weight: 600;
    color: #333;
  }
  
  .ant-table-thead > tr > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan]):before {
    display: none !important;
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f0f0f0;
    border-left: none !important;
    border-right: none !important;
    color: #666;
  }
  
  .ant-table-tbody > tr:hover > td {
    background: #f9f9f9;
  }
  
  .ant-table-container {
    width: 100%;
  }
`;

const StatusTag = styled.span<{ status: 'paid' | 'unpaid' }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.status === 'paid' ? '#e8f5e8' : '#fff3e0'};
  color: ${props => props.status === 'paid' ? '#2e7d32' : '#f57c00'};
`;

const BillingPage = () => {
  const { isCollapsed } = useNavigationStore();
  const navigate = useNavigate();

  const handleUpgradeClick = () => {
    navigate('/plan-selection');
  };
  // 더미 데이터
  const billingData = [
    {
      key: '1',
      date: '2025.4.20',
      orderNumber: '000000-000000',
      plan: 'Team 플랜 구독',
      amount: '₩149,000',
      status: 'unpaid' as const,
    },
    {
      key: '2',
      date: '2025.3.20',
      orderNumber: '000000-000000',
      plan: 'Team 플랜 구독',
      amount: '₩149,000',
      status: 'paid' as const,
    },
    {
      key: '3',
      date: '2025.2.20',
      orderNumber: '000000-000000',
      plan: 'Team 플랜 구독',
      amount: '₩149,000',
      status: 'paid' as const,
    },
    {
      key: '4',
      date: '2025.1.20',
      orderNumber: '000000-000000',
      plan: 'Team 플랜 구독',
      amount: '₩149,000',
      status: 'paid' as const,
    },
  ];

  const columns = [
    {
      title: '날짜',
      dataIndex: 'date',
      key: 'date',
      width: '12%',
    },
    {
      title: '주문번호',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: '20%',
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: '25%',
    },
    {
      title: '금액',
      dataIndex: 'amount',
      key: 'amount',
      width: '15%',
    },
    {
      title: '결제 상태',
      dataIndex: 'status',
      key: 'status',
      width: '18%',
      render: (status: 'paid' | 'unpaid') => (
        <StatusTag status={status}>
          {status === 'paid' ? '결제' : '미결제'}
        </StatusTag>
      ),
    },
    {
      title: '받기',
      key: 'action',
      width: '10%',
      render: () => (
        <Button type="text" icon={<DownloadOutlined />} size="small" />
      ),
    },
  ];

  return (
    <MainContainer>
      <Content $isCollapsed={isCollapsed}>
        <SideBar />
        <MainContent>
          <PageContainer>
            <KoHeadline>결제·구독 관리</KoHeadline>
            
            <ManagementSection>
              <SectionTitle>결제 관리</SectionTitle>
              <TopSection>
                <FreePlanSection>
                  <PlanHeaderSection>
                    <PlanContentArea>
                      <PlanTitle>Free 플랜</PlanTitle>
                      <PlanDescription>
                        월간 기능을 제한해서 쓸 수 있는 무료 플랜으로<br />
                        개인 프로젝트나 기능 테스트 용도에 가장 적합합니다.
                      </PlanDescription>
                    </PlanContentArea>
                    <UpgradeButton size="large" onClick={handleUpgradeClick}>
                      플랜 업그레이드
                    </UpgradeButton>
                  </PlanHeaderSection>
                  
                  <ProgressSection>
                    <ProgressLabel>35분 / 60분</ProgressLabel>
                    <Progress 
                      percent={58} 
                      strokeColor="#4F7EFF"
                      showInfo={false}
                      strokeWidth={6}
                    />
                  </ProgressSection>
                </FreePlanSection>
                
                <PaymentSection>
                  <PaymentHeaderSection>
                    <PaymentContentArea>
                      <PaymentTitle>결제정보</PaymentTitle>
                      <PaymentDescription>
                        등록된 결제 수단을 새롭게 추가하거나<br />
                        기존 정보를 수정할 수 있습니다.
                      </PaymentDescription>
                    </PaymentContentArea>
                    <ChangeButton>
                      결제 정보 변경
                    </ChangeButton>
                  </PaymentHeaderSection>
                  
                  <CardInfo>
                    <VisaIconWrapper>
                      <CreditCardOutlined style={{ fontSize: '20px', color: '#1a73e8' }} />
                    </VisaIconWrapper>
                    <CardDetails>
                      <CardNumber>****-****-1234-5678</CardNumber>
                      <CardExpiry>06/2026</CardExpiry>
                    </CardDetails>
                  </CardInfo>
                </PaymentSection>
              </TopSection>
            </ManagementSection>

            <BillingHistorySection>
              <BottomSection>
                <SectionTitle>결제 내역</SectionTitle>
                <DownloadButton icon={<DownloadOutlined />}>
                  전체 다운로드
                </DownloadButton>
              </BottomSection>

              <StyledTable 
                dataSource={billingData}
                columns={columns}
                pagination={false}
                size="middle"
              />
            </BillingHistorySection>
          </PageContainer>
          </MainContent>
        </Content>
      </MainContainer>
    );
  };

  export default BillingPage;